import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Contact,
  Github,
  Globe,
  Linkedin,
  Palette,
  QrCode,
  ShieldCheck,
  Twitter,
} from 'lucide-react';

/**
 * Public marketing landing at `/`. The audience is people who scanned a
 * friend's QR, saw the "Made with andmore" footer, and clicked through.
 * One-scroll hero → why → templates → final CTA. Light mode by default.
 */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-emerald-200/70">
      <TopBar />
      <Hero />
      <PainStrip />
      <Pillars />
      <TemplateShowcase />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Top bar
// ────────────────────────────────────────────────────────────────────────

function TopBar() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
        <Wordmark />
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/login"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors px-3 py-1.5"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 py-1.5 transition-colors duration-150 shadow-sm"
          >
            Make yours
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function Wordmark() {
  return (
    <Link to="/" className="flex items-center gap-2 group">
      <span className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
        <QrCode className="w-4 h-4 text-white" strokeWidth={2.4} />
      </span>
      <span className="flex items-baseline gap-1.5 leading-none">
        <span className="font-semibold tracking-tight text-slate-900">andmore</span>
        <span className="font-medium tracking-tight text-emerald-600">profile</span>
      </span>
    </Link>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Hero
// ────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft accent backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 0%, rgba(34,197,94,0.10) 0%, transparent 60%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        }}
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="motion-safe:animate-fade-up">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-full px-3 py-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              QRs that don't die
            </span>
            <h1 className="mt-5 text-4xl sm:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              The profile page
              <br />
              <span className="text-emerald-600">you scan to.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base sm:text-lg text-slate-600 leading-relaxed">
              One scan saves your contact, opens your links, and still resolves five conferences
              from now. The free tier doesn't expire — print your QR once, it keeps working.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-6 py-3 text-base shadow-md hover:shadow-lg transition-all duration-150"
              >
                Make your profile — free
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium px-3 py-2 text-sm"
              >
                See how it works
              </a>
            </div>
            <ul className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
              <Bullet>No expiring tier</Bullet>
              <Bullet>Custom colors and photo</Bullet>
              <Bullet>Save-to-contacts built in</Bullet>
            </ul>
          </div>

          {/* Phone mockup */}
          <div className="motion-safe:animate-fade-up" style={{ animationDelay: '120ms' }}>
            <PhoneMockup variant="spotlight" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="inline-flex items-center gap-1.5">
      <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={3} />
      {children}
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Pain strip — one-liner reframe
// ────────────────────────────────────────────────────────────────────────

function PainStrip() {
  return (
    <section className="bg-slate-900 text-slate-100">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-center leading-snug">
          Every "free" link-in-bio paywalls. Every QR generator expires.
          <br className="hidden sm:block" />
          <span className="text-emerald-400"> Your profile shouldn't.</span>
        </p>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Pillars — what you get
// ────────────────────────────────────────────────────────────────────────

function Pillars() {
  return (
    <section id="how" className="bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-emerald-600">
            Built for the long run
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Your profile. Yours forever. Looks like you.
          </h2>
          <p className="mt-3 text-slate-600 text-base sm:text-lg leading-relaxed">
            Most link-in-bio tools paywall the basics. Most QR tools expire on you.
            We're built around the opposite promise — a profile you can print, share, and forget about.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <Pillar
            icon={<ShieldCheck className="w-5 h-5" />}
            title="Yours forever"
            body="The QR you print today keeps resolving — no 'upgrade to keep your QR working,' no rug-pull, no expiration on the codes themselves. Free tier QRs don't die."
          />
          <Pillar
            icon={<Palette className="w-5 h-5" />}
            title="Looks like you"
            body="Three templates, your colors, your photo, your banner. Pick a vibe — clean, photo-led, or terminal-mono — in a tap."
          />
          <Pillar
            icon={<Contact className="w-5 h-5" />}
            title="More than a link"
            body="One scan can save you to their contacts, point at every social you have, and quietly track scans so you know what's working."
          />
        </div>
      </div>
    </section>
  );
}

function Pillar({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all duration-200">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700">
        {icon}
      </span>
      <h3 className="mt-4 text-lg font-semibold text-slate-900 tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Templates showcase — three phones, three vibes
// ────────────────────────────────────────────────────────────────────────

function TemplateShowcase() {
  return (
    <section className="bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wider text-emerald-600">
            Pick a vibe
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Three templates. Your accent. Your photo.
          </h2>
          <p className="mt-3 text-slate-600 text-base sm:text-lg leading-relaxed">
            Switch profiles between them in a tap. Your colors, your photo, your content — all three templates included on the free tier.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-6">
          <TemplateColumn name="Classic" caption="Clean default for any vibe.">
            <PhoneMockup variant="classic" />
          </TemplateColumn>
          <TemplateColumn name="Spotlight" caption="Banner-led with glassy cards.">
            <PhoneMockup variant="spotlight" />
          </TemplateColumn>
          <TemplateColumn name="Terminal" caption="Monospace, for the dev crowd.">
            <PhoneMockup variant="terminal" />
          </TemplateColumn>
        </div>
      </div>
    </section>
  );
}

function TemplateColumn({
  name,
  caption,
  children,
}: {
  name: string;
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center">
      {children}
      <p className="mt-5 text-sm font-semibold text-slate-900">{name}</p>
      <p className="text-xs text-slate-500 text-center max-w-[14rem]">{caption}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Final CTA
// ────────────────────────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section className="bg-white">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
        <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-slate-900">
          Your profile. <span className="text-emerald-600">Yours forever.</span>
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-base sm:text-lg text-slate-600">
          Takes a minute to set up. Outlives every conference badge you've ever printed.
        </p>
        <div className="mt-8 flex justify-center">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-7 py-3.5 text-base shadow-md hover:shadow-lg transition-all duration-150"
          >
            Make your profile — free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-500">No card required. Free tier doesn't expire.</p>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Footer
// ────────────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="border-t border-slate-100 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
        <Wordmark />
        <div className="flex items-center gap-5">
          <Link to="/login" className="hover:text-slate-900 transition-colors">
            Sign in
          </Link>
          <span className="text-slate-300">·</span>
          <span>Made to last.</span>
        </div>
      </div>
    </footer>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Phone mockup — bespoke per template, tuned for marketing legibility
// ────────────────────────────────────────────────────────────────────────

function PhoneMockup({ variant }: { variant: 'classic' | 'spotlight' | 'terminal' }) {
  return (
    <div
      className="relative mx-auto"
      style={{ width: 268, aspectRatio: '9 / 19' }}
      aria-hidden="true"
    >
      {/* Frame */}
      <div className="absolute inset-0 rounded-[2.4rem] bg-slate-900 p-2 shadow-[0_25px_60px_-20px_rgba(15,23,42,0.45)]">
        <div className="relative w-full h-full rounded-[1.9rem] overflow-hidden bg-white">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-900 rounded-full z-10" />
          {/* Content */}
          {variant === 'classic' && <ClassicMock />}
          {variant === 'spotlight' && <SpotlightMock />}
          {variant === 'terminal' && <TerminalMock />}
        </div>
      </div>
    </div>
  );
}

// ─── Classic mock — light, centered, accent-outlined buttons ───
function ClassicMock() {
  return (
    <div className="w-full h-full bg-white px-5 pt-12 pb-4 flex flex-col items-center text-center">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-500 ring-2 ring-white shadow flex items-center justify-center text-white font-semibold">
        AM
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-900">Andres M.</p>
      <p className="mt-0.5 text-[10px] text-slate-500 leading-tight">Building And More</p>
      <div className="mt-3 w-full bg-emerald-500 text-white rounded-lg py-2 text-[11px] font-semibold flex items-center justify-center gap-1 shadow-sm">
        <Contact className="w-3 h-3" /> Save my contact
      </div>
      <div className="mt-2.5 w-full space-y-1.5">
        <MockLinkRow icon={<Github className="w-3 h-3" />} label="github" />
        <MockLinkRow icon={<Linkedin className="w-3 h-3" />} label="linkedin" />
        <MockLinkRow icon={<Twitter className="w-3 h-3" />} label="x" />
        <MockLinkRow icon={<Globe className="w-3 h-3" />} label="andmore.dev" />
      </div>
    </div>
  );
}

function MockLinkRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-emerald-300 bg-white text-[11px] text-slate-700">
      <span className="text-emerald-600">{icon}</span>
      <span className="flex-1 truncate text-center">{label}</span>
    </div>
  );
}

// ─── Spotlight mock — banner header + glassy cards on dark ───
function SpotlightMock() {
  return (
    <div className="w-full h-full bg-[#0B1120] text-white">
      {/* Banner — bright at the top for visual interest, fades to dark before
          the avatar lands so the bright emerald circle reads against it. */}
      <div
        className="relative h-24 w-full"
        style={{
          background:
            'linear-gradient(180deg, #34d399 0%, #047857 35%, #042f1e 70%, #0B1120 100%)',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(11,17,32,0) 0%, rgba(11,17,32,0.45) 55%, rgba(11,17,32,0.95) 100%)',
          }}
        />
      </div>
      {/* `relative z-10` on this block keeps the overlapping avatar above
          the `position: relative` banner — without it, CSS paint order
          puts the banner on top and clips the avatar's upper half. */}
      <div className="relative z-10 -mt-8 px-4 flex flex-col items-center text-center">
        <div
          className="w-16 h-16 rounded-full bg-emerald-400 shadow-lg flex items-center justify-center text-[#0B1120] text-base font-bold"
          style={{ boxShadow: '0 0 0 3px #0B1120, 0 0 0 5px rgba(255,255,255,0.18), 0 14px 28px rgba(0,0,0,0.35)' }}
        >
          AM
        </div>
        <p className="mt-3 text-sm font-semibold">Andres Moreno</p>
        <p className="mt-0.5 text-[10px] text-white/60 leading-tight">
          Conference speaker & dev
        </p>
        {/* CTA */}
        <div className="mt-3 w-full bg-emerald-400 text-[#0B1120] rounded-xl py-2 text-[11px] font-semibold flex items-center justify-center gap-1.5 shadow-md">
          <span className="w-5 h-5 rounded-md bg-white/30 flex items-center justify-center">
            <Contact className="w-3 h-3" />
          </span>
          Save my contact
        </div>
        {/* Glassy cards */}
        <div className="mt-2.5 w-full space-y-1.5">
          <GlassCard icon={<Github className="w-3 h-3" />} label="github" />
          <GlassCard icon={<Linkedin className="w-3 h-3" />} label="linkedin" />
          <GlassCard icon={<Globe className="w-3 h-3" />} label="andmore.dev" />
        </div>
      </div>
    </div>
  );
}

function GlassCard({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-[11px] text-white">
      <span className="w-5 h-5 rounded-md bg-emerald-400/20 text-emerald-300 flex items-center justify-center">
        {icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
    </div>
  );
}

// ─── Terminal mock — mono, accent glow, comment block ───
function TerminalMock() {
  return (
    <div className="w-full h-full bg-[#0A0E0A] text-emerald-100 font-mono px-4 pt-12 pb-4">
      <p className="text-[9px] uppercase tracking-widest text-emerald-700">~/profile</p>
      <div className="mt-1 flex items-center gap-2">
        <div className="w-12 h-12 bg-emerald-500/10 flex items-center justify-center text-emerald-300 text-sm font-bold" style={{ outline: '1.5px solid #34d399', outlineOffset: '2px', boxShadow: '0 0 12px rgba(52,211,153,0.4)' }}>
          AM
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] truncate">
            <span className="text-emerald-400">&gt;&nbsp;</span>andres_m
            <span className="inline-block w-1 h-3 ml-0.5 bg-emerald-400 align-middle" />
          </p>
        </div>
      </div>
      <div className="mt-3 text-[9px] leading-relaxed">
        <div><span className="text-emerald-400">// </span>building and more</div>
        <div><span className="text-emerald-400">// </span>conference speaker</div>
      </div>
      {/* CTA */}
      <div
        className="mt-3 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px]"
        style={{
          border: '1px solid #34d399',
          backgroundColor: 'rgba(52,211,153,0.08)',
          boxShadow: '0 0 16px rgba(52,211,153,0.25)',
        }}
      >
        <span className="text-emerald-400">$</span>
        <span className="text-emerald-400">[</span>
        <span>save_contact.vcf</span>
        <span className="text-emerald-400">]</span>
      </div>
      <p className="mt-3 text-[9px] uppercase tracking-widest text-emerald-700">$ ls links/</p>
      <div className="mt-1.5 grid grid-cols-2 gap-1">
        <TermLinkBox label="github" />
        <TermLinkBox label="x" />
        <TermLinkBox label="blog" />
        <TermLinkBox label="rss" />
      </div>
    </div>
  );
}

function TermLinkBox({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1 px-1.5 py-1 text-[9px] border border-emerald-900/60">
      <span className="text-emerald-400">&gt;</span>
      <span className="text-emerald-300/70">[</span>
      <span className="truncate">{label}</span>
      <span className="text-emerald-300/70">]</span>
    </div>
  );
}
